// ── Publish Modal — vault → marketplace ───────────────────────────────
// Lets a user publish one of their strategies as a `strategy_listings` row.
// Freezes the current perf_snapshot from the latest backtest_runs metrics
// at publish time. Price is informational only at alpha (no Stripe wired).

const PUB_CATEGORIES = ["crypto", "equities", "futures", "options", "other"];

function PublishModal({ strategy, open, onClose, onPublished, onToast }) {
  const [title,   setTitle]   = React.useState(strategy?.name || "");
  const [blurb,   setBlurb]   = React.useState("");
  const [desc,    setDesc]    = React.useState("");
  const [category,setCategory]= React.useState("equities");
  const [tagsStr, setTagsStr] = React.useState("");
  const [price,   setPrice]   = React.useState(99);
  const [visibility, setVisibility] = React.useState("marketplace");
  const [perfSnapshot, setPerfSnapshot] = React.useState(null);
  const [loadingPerf, setLoadingPerf]   = React.useState(true);
  const [submitting,   setSubmitting]   = React.useState(false);
  const [errMsg,       setErrMsg]       = React.useState(null);

  // When modal opens for a strategy, load its latest backtest run & freeze metrics
  React.useEffect(() => {
    if (!open || !strategy?.id) return;
    setTitle(strategy.name || "");
    setLoadingPerf(true); setErrMsg(null); setPerfSnapshot(null);
    (async () => {
      try {
        const sb = window.sbClient;
        if (!sb) { setLoadingPerf(false); return; }
        const { data: { user } } = await sb.auth.getUser();
        if (!user) { setLoadingPerf(false); return; }
        const { data, error } = await sb
          .from("backtest_runs")
          .select("metrics, equity_curve, created_at")
          .eq("strategy_id", strategy.id)
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (!data) { setLoadingPerf(false); return; }
        const m = data.metrics || {};
        // 30D return from tail of equity_curve
        let d30 = null, d1 = null;
        const ec = data.equity_curve || [];
        if (ec.length >= 2) {
          const last = ec[ec.length - 1]?.v ?? null;
          if (last) {
            const head30 = ec[Math.max(0, ec.length - 30)]?.v ?? ec[0]?.v;
            const head1  = ec[Math.max(0, ec.length - 2)]?.v ?? ec[0]?.v;
            if (head30) d30 = ((last / head30) - 1) * 100;
            if (head1)  d1  = ((last / head1)  - 1) * 100;
          }
        }
        setPerfSnapshot({
          sharpe:     m.sharpe ?? null,
          sortino:    m.sortino ?? null,
          cagr:       m.cagr ?? null,
          maxDD:      m.maxDrawdown ?? null,
          totalReturn:m.totalReturn ?? null,
          winRate:    m.winRate ?? null,
          tradeCount: m.tradeCount ?? null,
          d1, d30,
          y1: m.cagr != null ? m.cagr * 100 : null,
          snapshot_at: data.created_at,
        });
      } catch (e) { setErrMsg(e.message); }
      setLoadingPerf(false);
    })();
  }, [open, strategy?.id]);

  if (!open) return null;

  const tags = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
  // Require at least one backtest run so subscribers see real metrics, not dashes.
  const canSubmit = !submitting
    && title.trim().length >= 3
    && blurb.trim().length >= 10
    && !loadingPerf
    && perfSnapshot != null;

  const handleSubmit = async () => {
    setSubmitting(true); setErrMsg(null);
    try {
      const sb = window.sbClient;
      const { data: { user } } = await sb.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const payload = {
        strategy_id: strategy.id,
        creator_id:  user.id,
        title:       title.trim(),
        blurb:       blurb.trim(),
        description: desc.trim() || null,
        asset:       strategy.market || null,
        exchange:    null,
        category,
        tags,
        price_monthly: +price || 0,
        visibility,                            // 'marketplace' or 'unlisted'
        status: visibility === "marketplace" ? "live" : "draft",
        perf_snapshot: perfSnapshot,
      };
      const { data, error } = await sb.from("strategy_listings").insert(payload).select().single();
      if (error) throw error;
      onToast?.(`Published "${title}" to marketplace`);
      window.gqTrack?.('publish_listing', { category, price_monthly: payload.price_monthly, visibility });
      onPublished?.(data);
      onClose?.();
    } catch (e) {
      setErrMsg(e.message);
      onToast?.(`Publish failed: ${e.message}`);
    }
    setSubmitting(false);
  };

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:6000,
      background:"rgba(0,0,0,0.55)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{
        maxWidth:560, width:"100%", maxHeight:"86vh", overflowY:"auto",
        background:"var(--bg-card)", border:"1px solid var(--border-strong)",
        borderRadius:"var(--r-lg)", padding:0,
      }}>
        {/* Header */}
        <div style={{padding:"18px 22px 14px", borderBottom:"1px solid var(--border)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:16,fontWeight:600,color:"var(--ink)"}}>Publish to marketplace</div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)",marginTop:2}}>
                Subscribers will see your most recent backtest performance, frozen at publish time.
              </div>
            </div>
            <button onClick={onClose} aria-label="Close" style={{background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontSize:22,padding:"0 4px"}}>×</button>
          </div>
        </div>

        {/* Performance preview */}
        <div style={{padding:"14px 22px", background:"rgba(17,255,153,0.04)", borderBottom:"1px solid var(--border)"}}>
          <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",letterSpacing:".06em",marginBottom:8}}>FROZEN PERFORMANCE SNAPSHOT</div>
          {loadingPerf ? (
            <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--subtle)"}}>Loading latest backtest…</div>
          ) : !perfSnapshot ? (
            <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--orange)",lineHeight:1.5}}>
              <b>No backtest run found.</b> You need at least one backtest before publishing — otherwise the marketplace card shows no metrics.
              <span style={{display:"block",marginTop:4,color:"var(--subtle)"}}>Close this, run a backtest, then come back.</span>
            </div>
          ) : (
            <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
              {[
                {l:"Sharpe", v: perfSnapshot.sharpe?.toFixed(2)},
                {l:"CAGR",   v: perfSnapshot.cagr != null ? (perfSnapshot.cagr*100).toFixed(1)+"%" : "—", c: (perfSnapshot.cagr||0) >= 0 ? "var(--green)" : "var(--red)"},
                {l:"Max DD", v: perfSnapshot.maxDD != null ? (perfSnapshot.maxDD*100).toFixed(1)+"%" : "—", c: "var(--red)"},
                {l:"Win rate", v: perfSnapshot.winRate != null ? (perfSnapshot.winRate*100).toFixed(0)+"%" : "—"},
                {l:"30D return", v: perfSnapshot.d30 != null ? (perfSnapshot.d30 >= 0 ? "+":"")+perfSnapshot.d30.toFixed(1)+"%" : "—"},
                {l:"Trades", v: perfSnapshot.tradeCount ?? "—"},
              ].map(x => (
                <div key={x.l}>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--subtle)"}}>{x.l}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:x.c||"var(--ink)",fontWeight:500}}>{x.v ?? "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form */}
        <div style={{padding:"16px 22px",display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Title</div>
            <input className="input" value={title} maxLength={80} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Solstice Trend"/>
          </div>
          <div>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Short blurb <span style={{color:"var(--subtle)"}}>· shown on marketplace card</span></div>
            <input className="input" value={blurb} maxLength={200} onChange={e=>setBlurb(e.target.value)} placeholder="One-line pitch under 200 characters"/>
          </div>
          <div>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Description <span style={{color:"var(--subtle)"}}>· full detail</span></div>
            <textarea className="input" value={desc} maxLength={2000} onChange={e=>setDesc(e.target.value)} rows={4} placeholder="Methodology, asset universe, when it works, when it doesn't…" style={{fontFamily:"var(--font-ui)",fontSize:13,resize:"vertical"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Category</div>
              <select className="input" value={category} onChange={e=>setCategory(e.target.value)}>
                {PUB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Price / month <span style={{color:"var(--subtle)"}}>· informational</span></div>
              <input className="input" type="number" min="0" step="1" value={price} onChange={e=>setPrice(+e.target.value)}/>
            </div>
          </div>
          <div>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Tags <span style={{color:"var(--subtle)"}}>· comma-separated</span></div>
            <input className="input" value={tagsStr} onChange={e=>setTagsStr(e.target.value)} placeholder="e.g. BTC, mean-rev, 4h"/>
          </div>
          <div>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Visibility</div>
            <div style={{display:"flex",gap:10}}>
              {[
                {v:"marketplace", l:"Marketplace",  d:"Discoverable to all members"},
                {v:"unlisted",    l:"Unlisted",     d:"Hidden until you share the link"},
              ].map(o => (
                <label key={o.v} style={{
                  flex:1, padding:"10px 12px", borderRadius:"var(--r-md)", cursor:"pointer",
                  border:`1px solid ${visibility===o.v?"rgba(217,70,239,0.4)":"var(--border)"}`,
                  background: visibility===o.v ? "rgba(217,70,239,0.04)" : "transparent",
                }}>
                  <input type="radio" checked={visibility===o.v} onChange={()=>setVisibility(o.v)} style={{marginRight:6}}/>
                  <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)",fontWeight:500}}>{o.l}</span>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",marginTop:2,marginLeft:18}}>{o.d}</div>
                </label>
              ))}
            </div>
          </div>
          {errMsg && (
            <div style={{padding:"8px 12px",background:"rgba(255,32,71,0.06)",border:"1px solid rgba(255,32,71,0.25)",borderRadius:"var(--r-md)",fontFamily:"var(--font-ui)",fontSize:12,color:"var(--red)"}}>
              {errMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"14px 22px",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)"}}>
            Subscribers are billed monthly via Stripe. Minimum price $0.50.
          </span>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

window.PublishModal = PublishModal;
